<template>
    <rkm-form
        ref="Form"
        v-if="model"
        show-legend
    >

        <rkm-area
            title="Paciente"
            type="fieldset"
        >
            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input
                        label="Nome (Nome social)"
                        type="text"
                        :value="form.informacoesPaciente.nome"
                        :readonly="true"
                        uppercase
                    >
                    </rkm-form-input>
                </rkm-col>
                <rkm-col cols="6">
                    <rkm-form-input
                        label="CNS"
                        :value="form.informacoesPaciente.cns"
                        :readonly="true"
                    />
                </rkm-col>
                <rkm-col cols="6">
                    <rkm-form-input
                        label="Data de nascimento"
                        :value="form.informacoesPaciente.dataNascimento"
                        :readonly="true"
                    >
                    </rkm-form-input>
                </rkm-col>
                <rkm-col cols="6">
                    <rkm-form-input
                        label="Idade"
                        :value="form.informacoesPaciente.pacienteIdade"
                        :readonly="true"
                    >
                    </rkm-form-input>
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-area
            type="fieldset"
            title="Profissional responsável"
        >

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :repository="repositories.usuario"
                        :requestQueryStringParams="autocompleteRequestParams.usuario"
                        :searchFields="fields.autocomplete.usuario"
                        :validations="validations.usuario"
                        text-field="cidadaoNome"
                        label="Profissional"
                        placeholder="Digite o nome ou número do conselho profissional"
                        v-model="model.profissional"
                        uppercase
                    />
                </rkm-col>
                <rkm-col cols="6">
                    <rkm-form-select
                        label="Unidade"
                        textField="nomeFantasia"
                        valueField="id"
                        v-model="model.unidade"
                        :options="form.unidades"
                        :validations="validations.unidade"
                        show-default-option
                    />
                </rkm-col>
                <rkm-col cols="6">
                    <rkm-form-select
                        :options="form.infraestruturas"
                        label="Local de atendimento"
                        show-default-option
                        text-field="nome"
                        v-model="model.infraestrutura"
                        :validations="validations.infraestrutura"
                        default-option-text="Selecione"
                        value-field="id"
                        :disabled="model.unidade == null"
                    />
                </rkm-col>
                <rkm-col cols="6">
                    <rkm-form-select
                        label="Código da equipe"
                        textField="descricao"
                        valueField="id"
                        v-model="model.equipe"
                        :options="form.equipes"
                        :validations="validations.equipe"
                        :disabled="model.infraestrutura == null"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        show-default-option
                        label="CBO"
                        textField="codigoNome"
                        valueField="id"
                        v-model="model.cbo"
                        :options="form.cbos"
                        :validations="validations.cbo"
                        :disabled="model.infraestrutura == null"
                    />
                </rkm-col>
                <rkm-col cols="6">
                    <rkm-form-date-picker
                        label="Data"
                        :max-date="form.dataAtual"
                        v-model="model.dataAtendimento"
                        :validations="validations.dataAtendimento"
                    />
                </rkm-col>
                <rkm-col cols="6">
                    <rkm-form-select
                        show-default-option
                        label="Tipo de local de atendimento"
                        text-field="descricao"
                        value-field="id"
                        :options="form.locaisAtendimento"
                        v-model="model.localAtendimento"
                        :validations="validations.localAtendimento"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-area
            type="fieldset"
            :title="form.titulo"
            show-legend
        >
            <Questionario
                v-if="form.questionario.length >= 1"
                ref="questionario"
                :questionario="form.questionario"
                :titulo="form.tituloArea"
            />
        </rkm-area>
    </rkm-form>
</template>

<script>
// Mixins
import { repository, formats, data, stringCase, object, error } from '@/mixins'

// Components
import Questionario from './components/questionario/Form.vue'

// Vuex
import { mapActions } from 'vuex'

// Enums.
import permissao from '@/enums/atencao-basica/parametrizacao/permissao'
import localAtendimento from '@/enums/atencao-basica/marcador-consumo-alimentar/localAtendimento'

export default {
    mixins: [repository, formats, data, stringCase, object, error],
    components: {
        Questionario
    },
    props: {
        id: { type: Number, default: null }
    },
    data: () => ({
        form: {
            informacoesPaciente: [],
            dataAtual: null,
            titulo: '',
            tituloArea: '',
            locaisAtendimento: [],
            cbos: [],
            questionario: [],
            equipes: [],
            unidades: [],
            infraestruturas: [],
            autocompleteFields: {
                profissional: [
                    { key: 'nome', searchable: true },
                ]
            }
        },
        model: {
            cbo: null,
            dataAtendimento: null,
            equipe: null,
            localAtendimento: null,
            paciente: null,
            profissional: null,
            unidade: null,
            infraestrutura: null,
            questionario: null,
            usuario: null,
        },
        enums: {
            permissao,
            localAtendimento
        },
        unwatchers: {
            modelProfissional: null,
            modelUnidadeId: null,
        },
    }),
    computed: {
        autocompleteRequestParams() {
            return {
                usuario: {
                    with_not_regularized: true,
                    with_atencao_basica_equipe: true,
                    atencao_basica_permissao: this.enums.permissao.MARCADOR_CONSUMO_ALIMENTAR
                },
            }
        },
        validations() {
            return {
                profissional: { required: true },
                cbo: { required: true },
                codigoEquipe: { required: true },
                unidade: { required: true },
                infraestrutura: { required: true },
                dataAtendimento: { required: true },
                localAtendimento: { required: true },
                equipe: { required: true }
            }
        },
        fields() {
            return {
                autocomplete: {
                    usuario: [
                        { key: "cadastroCidadao.nome", searchable: true },
                        { key: "cadastroUsuario.registroNumero", searchable: true },
                    ],
                    cid: [{ key: "nome", searchable: true }],
                },
            }
        },
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            if (this.id) {

                let url = this.$route.path.includes('create') ? 'create' : 'edit'

                const request = url == 'create'
                    ? this.repositories.marcadorConsumoAlimentar.create(this.id)
                    : this.repositories.marcadorConsumoAlimentar.edit(this.id)

                request
                    .then(({ data }) => {
                        this.form.informacoesPaciente = data.form.informacoesPaciente
                        this.form.titulo = data.form.titulo
                        this.form.tituloArea = data.form.tituloArea
                        this.form.dataAtual = data.form.maxDataAtual
                        this.form.locaisAtendimento = data.form.locaisAtendimento
                        this.form.questionario = data.form.perguntas
                        this.model.paciente = this.form.informacoesPaciente.id
                        this.form.informacoesPaciente.cns = this.form.informacoesPaciente.cns
                            ? this.formatCns(this.form.informacoesPaciente.cns)
                            : null

                        this.model.localAtendimento = this.enums.localAtendimento.UBS

                        if (data.model) {

                            this.form.questionario.respostasMarcadas = data.model.perguntas

                            this.model.localAtendimento = data.model.localAtendimentoId
                            this.model.dataAtendimento = data.model.dataAtendimento
                            this.model.profissional = data.model.profissional

                            this.model.unidade = data.model.unidadeId
                            this.model.infraestrutura = data.model.infraestruturaId
                            this.model.equipe = data.model.equipeId
                            this.model.cbo = data.model.cboId

                       } else {

                            this.model.dataAtendimento = this.getFormatedDate(data.form.dataAtual, 'YYYY-MM-DD')
                       }

                        if (this.model.profissional && this.model.profissional.id) {

                            this.getUnidades()

                            if (this.model.unidade) {

                                this.getInfraestruturas()
                                this.getEquipes()
                                this.getOcupacoes()
                            }
                        }

                        this.$nextTick(function () {this.addWatchers()})
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        },
        prepareData() {
            return this.camelObjectKeysToSnakeRecursive(this.clone(this.model))
        },
        save() {
            this.$refs.Form.validate()
                .then(valid => {

                    if (!valid) return

                    this.setLoading()

                    this.model.questionario = this.$refs.questionario.model

                    this.model.usuario = this.model.profissional.id

                    delete this.model.profissional

                    const url = this.$route.path.includes('create') ? 'create' : 'edit'

                    const request = url == 'create'
                        ? this.repositories.marcadorConsumoAlimentar.store(this.prepareData())
                        : this.repositories.marcadorConsumoAlimentar.update(this.id, this.prepareData())

                    request
                        .then(({ data }) => {
                            this.showSaveNotification()
                            this.$emit('save', data)
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        addWatchers() {

            this.addModelUnidadeIdWatcher()
            this.addModelProfissionalWatcher()
            this.addModelInfraestruturaIdWatcher()
        },
        addModelProfissionalWatcher() {

            this.unwatchers.modelProfissional = this.$watch('model.profissional', function (usuario) {

                if (usuario) {

                    this.getUnidades()
                }

                this.setCadastro('usuario')
            })
        },
        addModelUnidadeIdWatcher() {

            this.unwatchers.modelUnidadeId = this.$watch('model.unidade', function (unidadeId) {

                if (unidadeId) {

                    this.getInfraestruturas()
                }

                this.setCadastro('unidade')
            })
        },
        addModelInfraestruturaIdWatcher() {

            this.unwatchers.modelInfraestruturaId = this.$watch('model.infraestrutura', function (infraestruturaId) {

                if (infraestruturaId) {

                    this.getEquipes()
                    this.getOcupacoes()
                }

                this.setCadastro('infraestrutura')
            })
        },
        getUnidades() {

            this.setLoading()

            this.repositories.marcadorConsumoAlimentar.getUnidades({usuario_id: this.model.profissional.id})
                .then(({ data }) => {

                    this.form.unidades = data

                    if (data.length === 1){

                        this.model.unidade = data[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getInfraestruturas() {

            this.setLoading()

            this.repositories.marcadorConsumoAlimentar.getInfraestruturas({
                usuario_id: this.model.profissional.id,
                unidade_id: this.model.unidade
            })
                .then(({ data }) => {

                    this.form.infraestruturas = data

                    if (data.length === 1) {

                        this.model.infraestrutura = data[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getEquipes() {

            this.setLoading()

            this.repositories.marcadorConsumoAlimentar.getEquipes({
                usuario_id: this.model.profissional.id,
                infraestrutura_id: this.model.infraestrutura
            })
                .then(({ data }) => {

                    this.form.equipes = data

                    if (data.length === 1){

                        this.model.equipe = data[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getOcupacoes() {

            this.setLoading()

            this.repositories.marcadorConsumoAlimentar.getOcupacoes({
                usuario_id: this.model.profissional.id,
                infraestrutura_id: this.model.infraestrutura
            })
                .then(({ data }) => {

                    this.form.cbos = data

                    if (data.length === 1){

                        this.model.cbo = data[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        setCadastro(field) {

            if (field === 'usuario'){

                this.model.unidade = null
                this.model.infraestrutura = null
                this.model.equipe = null
                this.model.cbo = null
            } else if (field === 'unidade'){

                this.model.infraestrutura = null
                this.model.equipe = null
                this.model.cbo = null
            } else {

                this.model.equipe = null
                this.model.cbo = null
            }
        }
    },
    mounted() {
        this.model.questionario = this.$refs.questionario
    },
    created() {
        this.repositories = {
            marcadorConsumoAlimentar: this.getConectaSusRepository('atencaoBasica.marcadorConsumoAlimentar'),
            usuario: this.getConectaSusRepository('cadastro.sujeitoAtencao.usuario'),
        }
        this.load()
    },
}
</script>